<template>
  <div class="tw:relative tw:group tw:h-auto tw:w-full">
    <video
      :src="src"
      :muted="muted"
      :autoplay="autoplay"
      :controls="isIOSDevice"
      :loop="loop"
      :width="width"
      :height="height"
      :preload="preload"
      :playsinline="true"
      webkit-playsinline
      :poster="poster"
      @ended="onPlayerEnded"
      @play="onPlayerPlay"
      @pause="onPlayerPause"
      @timeupdate="onTimeUpdate"
      @loadedmetadata="onLoadedMetadata"
      ref="player"
      :class="[
        'tw:w-full tw:h-full',
        isRounded && 'tw:rounded-xl',
        controls && !isIOSDevice && 'tw:pb-[60px] tw:bg-black',
      ]"
      @click="!isIOSDevice && togglePlay()"
    />

    <!-- Pause Overlay - Shows when paused -->
    <div
      v-if="!playing && !isIOSDevice"
      @click="play"
      class="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center tw:bg-black/50 tw:cursor-pointer tw:transition-opacity"
      :class="isRounded && 'tw:rounded-xl'"
    >
      <button @click.stop="play" class="tw:rounded-full tw:p-4 tw:transition-all tw:hover:scale-110">
        <img :src="WhitePlayButton" alt="Play" class="tw:w-16 tw:h-16" />
      </button>
    </div>

    <!-- Custom Controls Overlay - Desktop only -->
    <div
      v-if="controls && !isIOSDevice"
      class="tw:absolute tw:inset-0 tw:flex tw:flex-col tw:justify-between tw:opacity-100 tw:pointer-events-none"
      :class="isRounded && 'tw:rounded-xl tw:overflow-hidden'"
    >
      <!-- Play/Pause Button in Center - Shows on hover -->
      <div class="tw:flex-1 tw:flex tw:items-center tw:justify-center">
        <button
          @click.stop="togglePlay"
          class="tw:rounded-full tw:p-4 tw:transition-all tw:hover:scale-110 tw:pointer-events-auto tw:opacity-0 tw:group-hover:opacity-100"
        >
          <img v-if="!playing" :src="PlayButton" alt="Play" class="tw:w-12 tw:h-12" />
          <img v-else :src="PauseButton" alt="Pause" class="tw:w-12 tw:h-12" />
        </button>
      </div>

      <!-- Bottom Controls Bar -->
      <div class="tw:bg-gradient-to-t tw:from-black/70 tw:to-transparent tw:p-4 tw:pointer-events-auto">
        <div class="tw:flex tw:items-center tw:gap-4">
          <!-- Small Play/Pause -->
          <button @click.stop="togglePlay" class="tw:text-white tw:hover:scale-110 tw:transition-transform">
            <svg v-if="!playing" class="tw:w-6 tw:h-6" fill="currentColor" viewBox="0 0 20 20">
              <path d="M5 4v12l10-6z" />
            </svg>
            <svg v-else class="tw:w-6 tw:h-6" fill="currentColor" viewBox="0 0 20 20">
              <path d="M5 4h3v12H5V4zm7 0h3v12h-3V4z" />
            </svg>
          </button>

          <!-- Progress Bar -->
          <div class="tw:flex-1 tw:flex tw:items-center tw:gap-2">
            <span class="tw:text-white tw:text-sm tw:min-w-[45px]">{{ formatTime(currentTime) }}</span>
            <div
              class="tw:relative tw:flex-1 tw:h-1 tw:bg-white/30 tw:rounded-full tw:cursor-pointer tw:group/progress"
              @click="seekTo"
              ref="progressBar"
            >
              <div
                class="tw:absolute tw:h-full tw:bg-white tw:rounded-full tw:group-hover/progress:h-2 tw:transition-all"
                :style="`width: ${progress}%`"
              >
                <div
                  class="tw:absolute tw:right-0 tw:top-1/2 tw:-translate-y-1/2 tw:w-3 tw:h-3 tw:bg-white tw:rounded-full tw:opacity-0 tw:group-hover/progress:opacity-100 tw:transition-opacity"
                ></div>
              </div>
            </div>
            <span class="tw:text-white tw:text-sm tw:min-w-[45px]">{{ formatTime(duration) }}</span>
          </div>

          <!-- Volume Control -->
          <div class="tw:flex tw:items-center tw:gap-2 tw:group/volume">
            <button @click.stop="toggleMute" class="tw:text-white tw:hover:scale-110 tw:transition-transform">
              <svg v-if="!isMuted && volume > 0" class="tw:w-6 tw:h-6" fill="currentColor" viewBox="0 0 20 20">
                <path
                  d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z"
                />
              </svg>
              <svg v-else class="tw:w-6 tw:h-6" fill="currentColor" viewBox="0 0 20 20">
                <path
                  d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z"
                />
              </svg>
            </button>
            <div
              class="tw:flex tw:items-center tw:h-8 tw:max-w-0 tw:group-hover/volume:max-w-[80px] tw:transition-all tw:duration-300 tw:overflow-hidden"
            >
              <input
                type="range"
                min="0"
                max="100"
                :value="volume"
                @input="setVolume"
                class="tw:w-20 tw:h-1 tw:cursor-pointer"
              />
            </div>
          </div>

          <!-- Fullscreen - Desktop only -->
          <button @click.stop="toggleFullscreen" class="tw:text-white tw:hover:scale-110 tw:transition-transform">
            <svg class="tw:w-6 tw:h-6" fill="currentColor" viewBox="0 0 20 20">
              <path
                d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 01-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 01-1.414-1.414L15.586 5H14a1 1 0 010-2zm-9 12a1 1 0 012 0v1.586l2.293-2.293a1 1 0 011.414 1.414L7.414 19H9a1 1 0 010 2H5a1 1 0 01-1-1v-4zm13 0a1 1 0 012 0v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 011.414-1.414L17 17.586V16z"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, nextTick, onUnmounted } from 'vue'

import * as Sentry from '@sentry/vue'

const PlayButton = new URL('/src/assets/img/studio-plus/icons/PlayIcon.svg', import.meta.url)
const WhitePlayButton = new URL('/src/assets/img/studio-plus/icons/WhitePlayIcon.svg', import.meta.url)
const PauseButton = new URL('/src/assets/img/studio-plus/icons/PauseIcon.svg', import.meta.url)

const props = defineProps({
  id: { type: [String, Number], required: true },
  src: { type: URL, required: true },
  controls: { type: Boolean, required: false, default: false },
  loop: { type: Boolean, required: false, default: true },
  width: { type: [Number, String], required: false, default: 500 },
  height: { type: [Number, String], required: false, default: 281 },
  autoplay: { type: Boolean, required: false, default: false },
  muted: { type: Boolean, required: false, default: true },
  poster: { type: String, required: false },
  preload: { type: String, required: false, default: 'auto' },
  playOnScroll: { type: Boolean, required: false, default: false },
  isRounded: { type: Boolean, required: false, default: false },
})

const playing = ref(false)
const player = ref(null)
const progressBar = ref(null)
const currentTime = ref(0)
const duration = ref(0)
const progress = ref(0)
const volume = ref(100)
const isMuted = ref(props.muted)
const isIOSDevice = ref(false)
let observer = null

const emit = defineEmits(['clickEventTriggered'])

const onPlayerPlay = () => {
  playing.value = true
}

const onPlayerPause = () => {
  playing.value = false
}

const onPlayerEnded = () => {
  playing.value = false
  if (props.loop) {
    play()
  }
}

const onTimeUpdate = () => {
  if (player.value) {
    currentTime.value = player.value.currentTime
    progress.value = (currentTime.value / duration.value) * 100
  }
}

const onLoadedMetadata = () => {
  if (player.value) {
    duration.value = player.value.duration
  }
}

const formatTime = seconds => {
  if (isNaN(seconds)) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const play = async () => {
  emit('clickEventTriggered', props.id)
  try {
    await player.value.play()
  } catch {
    playing.value = false
  }
}

const pause = () => {
  player.value?.pause()
}

const togglePlay = () => {
  if (playing.value) {
    pause()
  } else {
    play()
  }
}

const seekTo = event => {
  if (player.value && progressBar.value) {
    const rect = progressBar.value.getBoundingClientRect()
    const clickX = event.clientX - rect.left
    const percentage = clickX / rect.width
    player.value.currentTime = percentage * duration.value
  }
}

const toggleMute = () => {
  if (player.value) {
    player.value.muted = !player.value.muted
    isMuted.value = player.value.muted
  }
}

const setVolume = event => {
  if (player.value) {
    volume.value = event.target.value
    player.value.volume = volume.value / 100
    if (volume.value > 0) {
      player.value.muted = false
      isMuted.value = false
    }
  }
}

const toggleFullscreen = () => {
  if (!player.value) return

  const container = player.value.parentElement

  try {
    if (!document.fullscreenElement) {
      // Enter fullscreen
      if (container.requestFullscreen) {
        container.requestFullscreen()
      } else if (container.webkitRequestFullscreen) {
        container.webkitRequestFullscreen()
      } else if (container.mozRequestFullScreen) {
        container.mozRequestFullScreen()
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen()
      }
    }
  } catch (error) {
    Sentry.withScope(scope => {
      scope.setTag('feature', 'videoplayer')
      Sentry.captureException(error)
    })
  }
}

onMounted(async () => {
  isIOSDevice.value = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream

  await nextTick()
  if (props.autoplay && player.value) {
    setTimeout(() => {
      play()
    }, 100)
  }

  if (player.value) {
    player.value.volume = volume.value / 100
  }

  if (props.playOnScroll) {
    observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !playing.value) {
            play()
          } else if (!entry.isIntersecting && playing.value) {
            pause()
          }
        })
      },
      {
        threshold: 0.2,
        rootMargin: '0px',
      }
    )

    if (player.value) {
      observer.observe(player.value)
    }
  }
})

onUnmounted(() => {
  if (player.value) {
    pause()
  }

  if (observer && player.value) {
    observer.unobserve(player.value)
    observer.disconnect()
  }
})
</script>

<style scoped>
button {
  /* iOS tap fixes */
  -webkit-tap-highlight-color: transparent;
  -webkit-appearance: none;
  cursor: pointer;
  touch-action: manipulation;
}

.volume-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 80px;
  height: 4px;
  background: white;
  border-radius: 2px;
  outline: none;
  cursor: pointer;
}

.volume-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  background: #ffffff;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
  border: 2px solid #ffffff;
}

.volume-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  background: #ffffff;
  border-radius: 50%;
  cursor: pointer;
  border: none;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
}

.volume-slider::-webkit-slider-thumb:hover {
  background: #f0f0f0;
  transform: scale(1.1);
}

.volume-slider::-moz-range-thumb:hover {
  background: #f0f0f0;
  transform: scale(1.1);
}
</style>
